import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SITE, IMAGES } from "@/data/site";
import { Reveal, SectionHead, SpecChip } from "@/components/Reveal";

const PROCESSES = [
  { n: "01", t: "5-Axis & Heavy CNC Coiling", d: "CNC coilers for precision springs from 0.10 mm, and heavy hot-winding lines forming bar up to 40.00 mm into springs to 350 mm OD and 800 mm free length." },
  { n: "02", t: "Heat Treatment", d: "In-house hardening, tempering, stress relieving and precipitation hardening — validated cycles for carbon steels, alloy steels and nickel superalloys like Inconel X-750 and 17-7PH." },
  { n: "03", t: "Grinding & Shot Peening", d: "Closed ends ground square for stable seating; shot peening to induce compressive surface stress and multiply fatigue life on high-cycle springs." },
  { n: "04", t: "Crack Detection", d: "Magnaflux magnetic particle inspection on critical and heavy springs — the surface defects you cannot see are the ones that kill fatigue life." },
  { n: "05", t: "Load & Deflection Testing", d: "Digital load testers at 5 kg, 300 kg, 5 MT and 20 MT capacities. Every critical spring certified at its working heights before dispatch." },
  { n: "06", t: "Surface Finishing", d: "Oiling, zinc plating, passivation, industrial painting and powder coating systems matched to the service environment." },
];

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>Manufacturing Capability | MESCO Springs — Spring Manufacturer Mumbai India Since 1969</title>
        <meta name="description" content="MESCO Springs capability: 5-axis CNC coiling, hot winding to 40mm wire, in-house heat treatment, shot peening, Magnaflux crack detection, load testing to 20 MT. ISO 9001:2015 certified spring plant in Mumbai, India." />
      </Helmet>
      <div className="relative pt-44 pb-20 overflow-hidden" data-testid="capability-hero">
        <img src={IMAGES.shopfloor3} alt="MESCO Springs plant" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FEFEFE] via-[#FEFEFE]/85 to-white/50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-mono-spec text-[11px] tracking-[0.35em] uppercase text-[#334772]">[ CAPABILITY ]</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] text-[#334772] mt-6">
              55+ years of<br />spring engineering
            </h1>
            <p className="mt-6 max-w-2xl text-[#706F70] leading-relaxed">
              {SITE.fullName} — family owned, proudly Indian, ISO 9001:2015 certified. Single-source and preferred
              spring supplier to major OEMs, serving industries from elevators to oil & gas since 1969.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              <SpecChip>ISO 9001:2015</SpecChip>
              <SpecChip>EST. 1969</SpecChip>
              <SpecChip>5000+ CUSTOMERS</SpecChip>
              <SpecChip>2000+ TONNES / YEAR</SpecChip>
              <SpecChip>135+ EMPLOYEES</SpecChip>
            </div>
          </Reveal>
        </div>
      </div>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHead index="01" label="Plant & Process" title="From wire to certified spring" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#334772]/10 border border-[#334772]/15">
            {PROCESSES.map((p, i) => (
              <Reveal key={p.n} delay={(i % 3) * 0.08}>
                <div className="crosshair-card bg-[#F3F5F8] p-8 h-full group hover:bg-[#F3F5F8] transition-colors duration-300">
                  <span className="font-display text-5xl text-[#334772]/25 group-hover:text-[#334772]/60 transition-colors duration-300">{p.n}</span>
                  <h3 className="font-display text-2xl text-[#334772] uppercase tracking-wide mt-3">{p.t}</h3>
                  <p className="mt-3 text-sm text-[#706F70] leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#F3F5F8] border-y border-[#334772]/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionHead index="02" label="Confidence in the Field" title="Trusted where failure is not an option" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-[#706F70] leading-relaxed">
                Even minutes of equipment downtime cause major losses. MESCO springs work in the harshest conditions
                without failure — in the equipment of clients such as BHEL and IOCL, and in OEM machinery exported
                worldwide. We use SAE, ASTM, JIS and IS grade raw materials with full mill traceability.
              </p>
              <p className="mt-4 text-[#706F70] leading-relaxed">
                Pan-India dispatch from Mumbai and exports worldwide, with EN 10204 3.1 material certificates, load
                test documentation and third-party inspection support on critical orders.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="crosshair-card border border-[#334772]/15 overflow-hidden">
              <img src={IMAGES.shopfloor1} alt="Heavy springs at MESCO works" className="w-full h-80 object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 text-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-5xl sm:text-6xl uppercase leading-[0.9] text-[#334772]">Put us on your vendor list</h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/enquiry" data-testid="capability-rfq-btn" className="group inline-flex items-center gap-3 px-8 py-4 bg-[#334772] text-white text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#2A3C60] transition-colors duration-200">
                Send an RFQ <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a href={SITE.mainSite} target="_blank" rel="noopener noreferrer" data-testid="capability-mainsite-btn" className="inline-flex items-center gap-3 px-8 py-4 border border-[#334772]/40 text-[#334772] text-sm font-bold uppercase tracking-[0.15em] hover:bg-[#334772] hover:text-white transition-colors duration-200">
                mescosprings.com <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
